// @ts-check
import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core'; // eslint-disable-line no-unused-vars
import DatePicker, { ReactDatePickerProps } from 'react-datepicker'; // eslint-disable-line no-unused-vars
import { ErrorMessage, useField } from 'formik';
import { mmDdYyyy } from 'preferral-common/utils/dateFormatters';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { IconContainer } from 'preferral-ui/components/formik/IconContainer';
import {
  HorizontalField,
  VerticalField,
} from 'preferral-ui/components/FieldStructure';
import './DatePickerField.css';

/** @typedef {{
  value: string;
  icon?: IconProp;
  inputProps?: Partial<ReactDatePickerProps>;
  className?: string;
  placeholder?: string;
  onChange(newValue: string): void;
  onBlur?(e: any): void;
  disabled?: boolean;
}} InputDatePickerProps */

/** @type { React.FC<InputDatePickerProps> } */
export const InputDatePicker = (props) => {
  const {
    value,
    onChange,
    onBlur,
    icon,
    inputProps = {},
    className = '',
    placeholder = 'mm/dd/yyyy',
    disabled = false,
  } = props;

  /**
   *
   * @param {Date | null} selectedDate
   */
  const handleChange = (selectedDate) => {
    const dateString = !!selectedDate ? mmDdYyyy(selectedDate) : '';
    onChange(dateString);
  };

  /**
   * NB: `className` can be overridden by `inputProps`.
   */

  /* eslint-disable max-len */
  return (
    <div
      className={`InputDatePicker rounded-md shadow-sm relative ${
        !!icon ? 'has-icons-left' : ''
      }`}
    >
      <DatePicker
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        className={`appearance-none block h-auto w-full m-0 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 ${className}`}
        placeholderText={placeholder}
        disabled={disabled}
        {...inputProps}
      />
      {icon && (
        <IconContainer className="text-gray-500">
          <FAIcon icon={icon} />
        </IconContainer>
      )}
    </div>
  );
  /* eslint-enable max-len */
};

/** @typedef {{
  name: string;
  id?: string;
  placeholder?: string;
  autoFocus?: boolean;
  icon?: IconProp;
  className?: string;
  inputProps?: ReactDatePickerProps;
  disabled?: boolean;
}} DatePickerInputProps */

/** @type { React.FC<DatePickerInputProps> } */
export const DatePickerInput = (props) => {
  const {
    id,
    name,
    icon,
    autoFocus = false,
    className = '',
    inputProps = {},
    disabled,
  } = props;

  const [field, meta, helpers] = useField(name);
  const { value, onBlur } = field;
  const { setValue } = helpers;

  return (
    <>
      <InputDatePicker
        inputProps={{
          id: id || name,
          name,
          autoFocus,
          ...inputProps,
        }}
        icon={icon}
        className={
          meta && meta.touched && meta.error
            ? `border border-red-500 ${className}`
            : className
        }
        value={value}
        onChange={setValue}
        onBlur={onBlur}
        disabled={disabled}
      />
      <ErrorMessage
        component="p"
        name={name}
        className="mt-2 text-xs italic text-red-500"
      />
    </>
  );
};

/** @typedef {{
  label: string;
  indicateOptional?: boolean;
} & DatePickerInputProps } DatePickerFieldProps */

/** @type { React.FC<DatePickerFieldProps> } */
export const DatePickerField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <VerticalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <DatePickerInput {...rest} />
    </VerticalField>
  );
};

/** @type { React.FC<DatePickerFieldProps> } */
export const HorizontalDatePickerField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <HorizontalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <DatePickerInput {...rest} />
    </HorizontalField>
  );
};
